import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add,
  Refresh,
  Edit,
  Delete,
  MoreVert,
  Search,
  Assignment,
  Download,
} from "@mui/icons-material";
import {
  getAllPartners,
  deletePartner,
  updatePartnerGoals,
  downloadAllPartnersExcel,
} from "../../services/Api";
import { useNavigate } from "react-router-dom";
import DeleteConfirm from "../../modals/DeleteConfirm";

const GetAllPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination and Search state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSelectedPartner, setMenuSelectedPartner] = useState(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Global Goals state
  const [globalGoals, setGlobalGoals] = useState(0);
  const [goalsModalOpen, setGoalsModalOpen] = useState(false);
  const [goalsInput, setGoalsInput] = useState("");
  const [goalsLoading, setGoalsLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 1000);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllPartners(
        page + 1,
        rowsPerPage,
        debouncedSearch,
      );
      if (response.success || response.data) {
        const partnersList = response.data || [];
        setPartners(partnersList);
        setTotalRows(
          response?.totalRecords ||
          response?.totalItems ||
          response?.totalCount ||
          response?.totalDocs ||
          response?.total ||
          response?.data?.totalRecords ||
          response?.data?.totalItems ||
          response?.data?.totalCount ||
          response?.data?.totalDocs ||
          response?.data?.total ||
          response?.data?.totalElements ||
          response?.data?.length ||
          response.totalItems ||
          response.total ||
          partnersList.length ||
          0,
        );

        if (response.dailyGoal !== undefined) {
          setGlobalGoals(response.dailyGoal);
        } else if (
          partnersList.length > 0 &&
          partnersList[0].dailyGoal !== undefined
        ) {
          setGlobalGoals(partnersList[0].dailyGoal);
        }
      } else {
        setError(response?.message || "Failed to fetch partners");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  const handleMenuOpen = (event, partner) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedPartner(partner);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSelectedPartner(null);
  };

  const handleDeleteClick = () => {
    setSelectedPartner(menuSelectedPartner);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deletePartner(
        selectedPartner.id || selectedPartner._id,
      );
      if (response.success) {
        setDeleteDialogOpen(false);
        fetchPartners();
      } else {
        console.error("Failed to delete partner");
      }
    } catch (err) {
      console.error("An error occurred while deleting the partner:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGoals = async () => {
    try {
      setGoalsLoading(true);
      const newGoal = parseInt(goalsInput) || 0;
      const response = await updatePartnerGoals(newGoal);
      if (response.success || response.data || response.message) {
        setGoalsModalOpen(false);
        setGlobalGoals(response.goals !== undefined ? response.goals : newGoal);
        // Skipping fetchPartners() to prevent race condition with stale backend data
      } else {
        console.error(response.message || "Failed to update global goals");
      }
    } catch (err) {
      console.error("An error occurred while updating goals:", err);
    } finally {
      setGoalsLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await downloadAllPartnersExcel();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "partners.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Failed to download Excel file", error);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, mb: 1 }}>
          Partner Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage delivery partners and delivery agents directly on the platform
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              placeholder="Search partners..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  backgroundColor: "rgba(0,0,0,0.02)",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              onClick={() => {
                setPartners([]);
                fetchPartners();
              }}
              disabled={loading}
              sx={{ color: "primary.main" }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <Refresh />
              )}
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadExcel}
              sx={{
                borderRadius: "12px",
                borderWidth: "1px",
                px: 3,
                py: 0.9,
                textTransform: "none",
                backgroundColor: "#9C27B0",
                borderColor: "#9C27B0",
                color: "white",
                "&:hover": { borderWidth: "1px" },
              }}
            >
              Download
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => navigate("/partners/create")}
              sx={{
                px: 3,
                py: 1,
                backgroundColor: "primary.main",
                fontWeight: "bold",
                borderRadius: "12px",
                textTransform: "none",
              }}
            >
              Register Partner
            </Button>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<Assignment />}
              onClick={() => {
                setGoalsInput(globalGoals || 0);
                setGoalsModalOpen(true);
              }}
              sx={{
                borderRadius: "10px",
                borderWidth: "2px",
                px: 3,
                py: 0.9,
                textTransform: "none",
                "&:hover": { borderWidth: "2px" },
              }}
            >
              Add Goals
            </Button>
            {globalGoals !== null && globalGoals !== undefined && (
              <Box
                sx={{
                  backgroundColor: "secondary.main",
                  color: "white",
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  fontWeight: 800,
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                }}
              >
                Goals: {globalGoals}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "text.border",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table
            sx={{ minWidth: 650 }}
            size="medium"
            aria-label="partners table"
          >
            <TableHead sx={{ backgroundColor: "rgba(0,0,0,0.02)" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Gender</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Partner Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>COD Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                    <Typography color="text.secondary">
                      {debouncedSearch
                        ? `No partners matching "${debouncedSearch}"`
                        : "No partners found"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow
                    key={partner.id || partner._id}
                    hover
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {partner.name}
                    </TableCell>
                    <TableCell>{partner.mobile}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {partner.gender}
                    </TableCell>
                    <TableCell>
                      {partner.partnerType ? (
                        <Chip
                          label={partner.partnerType}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(0,0,0,0.02)",
                            border: "1px solid",
                            borderColor: "text.border",
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {partner.categoryName ? (
                        <Chip
                          label={partner.categoryName}
                          size="small"
                          sx={{
                            backgroundColor: "primary.light",
                            color: "primary.contrastText",
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={partner.isActive ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          backgroundColor: partner.isActive
                            ? "success.main"
                            : "grey.400",
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>{partner.codAmount}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, partner)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        elevation={1}
        PaperProps={{
          sx: {
            minWidth: 150,
            border: "1px solid",
            borderColor: "text.border",
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(
              `/partners/${menuSelectedPartner.id || menuSelectedPartner._id}/bookings`,
              { state: { partner: menuSelectedPartner } },
            );
            handleMenuClose();
          }}
        >
          <MuiListItemIcon>
            <Assignment fontSize="small" color="primary" />
          </MuiListItemIcon>
          Bookings List
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(
              `/partners/modify/${menuSelectedPartner.id || menuSelectedPartner._id}`,
              { state: { partner: menuSelectedPartner } },
            );
            handleMenuClose();
          }}
        >
          <MuiListItemIcon>
            <Edit fontSize="small" color="primary" />
          </MuiListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <MuiListItemIcon>
            <Delete fontSize="small" color="error" />
          </MuiListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <DeleteConfirm
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Partner?"
        message={`Are you sure you want to delete the partner "${selectedPartner?.name}"?`}
      />

      {/* Global Goals Modal */}
      <Dialog open={goalsModalOpen} onClose={() => setGoalsModalOpen(false)}>
        <DialogTitle>Update Partner Goals</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the universal daily/weekly goals for all delivery partners.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Goals Target"
            type="number"
            fullWidth
            value={goalsInput}
            onChange={(e) => setGoalsInput(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGoalsModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateGoals}
            variant="contained"
            disabled={goalsLoading || goalsInput === ""}
            startIcon={
              goalsLoading && <CircularProgress size={16} color="inherit" />
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GetAllPartners;
